import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import {
  useFetchTenantAppUserTypesQuery,
  useAddTenantAppUserTypeMutation,
  useUpdateTenantAppUserTypeMutation,
} from "../../../features/admin/AppUserType/appUserTypeApi";
import { useRenameAppUserTypeMutation } from "../../../features/tenant/appUserType/appUserTypeApi";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";

const AppUserType = () => {
  const token = sessionStorage.getItem("access_token");

  // Fetch all vendors (tenants)
  const {
    data: vendorsData,
    isLoading: vendorsLoading,
    error: vendorsError,
  } = useFetchVendorsQuery(token);

  const [selectedTenant, setSelectedTenant] = useState(null);

  // Fetch app user types for the selected tenant
  const {
    data: tenantUserTypesData,
    isLoading: isListLoading,
    refetch: refetchList,
  } = useFetchTenantAppUserTypesQuery(
    {
      token,
      slug: selectedTenant?.tenant_id, // Use tenant_id as slug
    },
    { skip: !selectedTenant }
  );

  // Mutations
  const [addTenantAppUserType] = useAddTenantAppUserTypeMutation();
  const [updateTenantAppUserType] = useUpdateTenantAppUserTypeMutation();
  const [uploadSingleFile, { isLoading: isUploading }] = useUploadSingleFileMutation();

  const [renameAppUserType] = useRenameAppUserTypeMutation();

  // Rename modal state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameRow, setRenameRow] = useState(null);
  const [newUserType, setNewUserType] = useState("");

  const handleOpenRename = (row) => {
    setRenameRow(row);
    setNewUserType("");
    setShowRenameModal(true);
  };

  const handleRenameSubmit = async (e) => {
    e.preventDefault();
    if (!newUserType.trim()) {
      toast.error("New user type code is required");
      return;
    }
    try {
      toast.loading("Renaming user type...", { id: "renaming-user-type" });
      await renameAppUserType({
        token,
        tenant_id: selectedTenant.tenant_id,
        body: {
          user_type_id: renameRow.id,
          old_user_type: renameRow.user_type,
          new_user_type: newUserType.trim(),
        },
      }).unwrap();
      toast.dismiss("renaming-user-type");
      toast.success("User type renamed successfully!");
      setShowRenameModal(false);
      refetchList();
    } catch (error) {
      toast.dismiss("renaming-user-type");
      toast.error(error?.data?.message || "Failed to rename user type");
    }
  };

  // Modals state
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  // Form states
  const [userType, setUserType] = useState("");
  const [name, setName] = useState("");
  const [userTypeLogo, setUserTypeLogo] = useState("");
  const [status, setStatus] = useState("1");

  useEffect(() => {
    if (vendorsError) {
      toast.error("Failed to load tenants");
    }
  }, [vendorsError]);

  const resetForm = () => {
    setUserType("");
    setName("");
    setUserTypeLogo("");
    setStatus("1");
    setCurrentId(null);
  };

  const handleOpenAdd = () => {
    setIsEditMode(false);
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (row) => {
    setIsEditMode(true);
    setCurrentId(row.id);
    setUserType(row.user_type || "");
    setName(row.name || "");
    setUserTypeLogo(row.user_type_logo || "");
    setStatus(row.status || "1");
    setShowModal(true);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      toast.loading("Uploading image...", { id: "uploading-logo" });
      const response = await uploadSingleFile({ body: formData }).unwrap();
      toast.dismiss("uploading-logo");
      toast.success("Logo uploaded successfully!");
      setUserTypeLogo(response.body.fileLink);
    } catch (error) {
      toast.dismiss("uploading-logo");
      toast.error("Logo upload failed");
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTenant) {
      toast.error("Please select a tenant first");
      return;
    }

    if (!isEditMode && !userType) {
      toast.error("User Type is required");
      return;
    }

    const payload = {
      user_type: userType,
      name,
      user_type_logo: userTypeLogo || null,
      status,
    };

    try {
      toast.loading("Saving app user type...", { id: "saving-user-type" });

      if (isEditMode) {
        await updateTenantAppUserType({
          token,
          slug: selectedTenant.tenant_id, // Use tenant_id as slug
          id: currentId,
          body: payload,
        }).unwrap();
        toast.dismiss("saving-user-type");
        toast.success("App User Type updated successfully!");
      } else {
        await addTenantAppUserType({
          token,
          slug: selectedTenant.tenant_id, // Use tenant_id as slug
          body: payload,
        }).unwrap();
        toast.dismiss("saving-user-type");
        toast.success("App User Type created successfully!");
      }

      setShowModal(false);
      refetchList();
    } catch (error) {
      toast.dismiss("saving-user-type");
      toast.error(error?.data?.message || "Failed to save user type");
      console.error(error);
    }
  };

  const columns = [
    {
      name: "S.No",
      selector: (row, idx) => idx + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Logo",
      cell: (row) => (
        row.user_type_logo ? (
          <img
            src={row.user_type_logo}
            alt="logo"
            style={{ height: "35px", width: "35px", objectFit: "contain" }}
            className="rounded border p-1"
          />
        ) : (
          <span className="text-gray-400">No Logo</span>
        )
      ),
      width: "90px",
    },
    {
      name: "Type Code",
      selector: (row) => row.user_type,
      sortable: true,
    },
    {
      name: "Display Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (row.status === "1" ? "Active" : "Inactive"),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() => handleOpenEdit(row)}
          >
            Edit
          </button>
          <button
            className="btn btn-outline-warning btn-sm"
            onClick={() => handleOpenRename(row)}
          >
            Rename
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "160px",
    },
  ];

  return (
    <div className="main-wrapper">
      <div className="page-content">
        <div className="card p-4">
          <div className="borderbtm d-flex justify-between items-center mb-3">
            <div>
              <h4 className="mb-3 mb-md-0 card-title">Welcome to App Users Master</h4>
            </div>
            {selectedTenant && (
              <div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-icon-text text-nowrap"
                  onClick={handleOpenAdd}
                >
                  Add App User Type
                </button>
              </div>
            )}
          </div>

          <div className="card-body p-0">
            <div className="row frmds mb-4">
              <div className="col-12 col-xl-6 col-xs-12 my-2">
                <label className="">Client</label>
                <select
                  className="js-example-basic-single w-100 rounded-lg shadow-sm shadow-black px-2"
                  value={selectedTenant ? selectedTenant.tenant_id : ""}
                  onChange={(e) => {
                    const vendor = vendorsData?.body.find((v) => String(v.tenant_id) === String(e.target.value));
                    setSelectedTenant(vendor || null);
                  }}
                >
                  <option className="rounded-lg shadow-sm shadow-black px-2" value="">
                    Select Client
                  </option>
                  {vendorsLoading && <option disabled>Loading...</option>}
                  {vendorsData &&
                    vendorsData.body.map((vendor) => (
                      <option
                        key={vendor.tenant_id}
                        value={vendor.tenant_id}
                        className="rounded-lg shadow-sm shadow-black px-2"
                      >
                        {vendor.name.charAt(0).toUpperCase() + vendor.name.slice(1)}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {!selectedTenant ? (
                  <div className="text-center py-5 text-gray-500 font-medium">
                    Please select a client from the dropdown above to view and manage app user types.
                  </div>
                ) : isListLoading ? (
                  <div className="text-center py-5 text-gray-500 font-medium">
                    Loading App User Types...
                  </div>
                ) : (
                  <DataTable
                    title={`User Types for ${selectedTenant.name.charAt(0).toUpperCase() + selectedTenant.name.slice(1)}`}
                    data={tenantUserTypesData?.body || []}
                    columns={columns}
                    pagination
                    highlightOnHover
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rename Modal */}
      <ModalComponent show={showRenameModal} setShow={setShowRenameModal} size="md">
        <div className="p-4">
          <h4 className="border-b pb-2 mb-4 font-bold text-lg">Rename User Type</h4>
          <form onSubmit={handleRenameSubmit}>
            <div className="mb-3">
              <InputComponent
                name="Current Type Code"
                type="text"
                value={renameRow?.user_type || ""}
                setValue={() => {}}
                disabled={true}
              />
            </div>
            <div className="mb-3">
              <InputComponent
                name="New Type Code"
                type="text"
                value={newUserType}
                setValue={setNewUserType}
                required={true}
                placeholder="Enter new lowercase type code"
              />
            </div>
            <div className="d-flex justify-content-end gap-2 pt-3 border-top mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowRenameModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-warning">
                Rename
              </button>
            </div>
          </form>
        </div>
      </ModalComponent>

      {/* Add / Edit Modal */}
      <ModalComponent show={showModal} setShow={setShowModal} size="xl">
        <div className="p-4">
          <h4 className="border-b pb-2 mb-4 font-bold text-lg">
            {isEditMode ? "Edit App User Type" : "Add App User Type"}
          </h4>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <InputComponent
                  name="User Type Code (e.g. mechanic)"
                  type="text"
                  value={userType}
                  setValue={setUserType}
                  required={!isEditMode}
                  disabled={isEditMode}
                  placeholder="Enter lowercase type code"
                />
              </div>
              <div className="col-md-6 mb-3">
                <InputComponent
                  name="Display Name"
                  type="text"
                  value={name}
                  setValue={setName}
                  required={true}
                  placeholder="Enter visual display name"
                />
              </div>

              <div className="col-md-6 mb-3">
                <div className="w-full">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    Upload Logo Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={handleLogoUpload}
                  />
                  {userTypeLogo && (
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <img
                        src={userTypeLogo}
                        alt="Preview"
                        style={{ height: "64px", width: "64px", objectFit: "contain" }}
                        className="border rounded p-1"
                      />
                      <span className="text-xs text-gray-500 break-all">{userTypeLogo}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="w-full">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    Status
                  </label>
                  <select
                    className="form-control"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 pt-3 border-top mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isUploading}
              >
                {isUploading ? "Uploading Logo..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </ModalComponent>
    </div>
  );
};

export default AppUserType;
